import { cloneElement, forwardRef, isValidElement, useId } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../lib/cn';
import styles from './forms.module.css';

/**
 * Label + control + hint/error. The control receives id / aria-* automatically
 * when it's a single element.
 */
export function Field({ label, hint, error, required, optional, htmlFor, className, children, action }) {
  const autoId = useId();
  const id = htmlFor || `f${autoId.replace(/:/g, '')}`;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  const isComponent = isValidElement(children) && typeof children.type !== 'string';
  const control =
    isValidElement(children) && !htmlFor
      ? cloneElement(children, {
          id: children.props.id || id,
          'aria-invalid': error ? true : undefined,
          'aria-describedby': describedBy,
          ...(isComponent ? { invalid: children.props.invalid ?? Boolean(error) } : null),
        })
      : children;

  return (
    <div className={cn(styles.field, className)}>
      {(label || action) && (
        <div className={styles.labelRow}>
          {label && (
            <label className={styles.label} htmlFor={children?.props?.id || id}>
              {label}
              {required && <span className={styles.required} aria-hidden="true"> *</span>}
              {optional && <span className={styles.optional}> (optional)</span>}
            </label>
          )}
          {action}
        </div>
      )}
      {control}
      {error ? (
        <p className={styles.error} id={errorId} role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className={styles.hint} id={hintId}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export const Input = forwardRef(function Input(
  { leading, trailing, invalid, size = 'md', className, inputClassName, ...rest },
  ref
) {
  if (!leading && !trailing) {
    return (
      <input
        ref={ref}
        className={cn(styles.control, styles[size], invalid && styles.invalid, className)}
        {...rest}
      />
    );
  }
  return (
    <div className={cn(styles.group, styles[size], invalid && styles.invalid, rest.disabled && styles.disabled, className)}>
      {leading && <span className={styles.adornment}>{leading}</span>}
      <input ref={ref} className={cn(styles.bare, inputClassName)} {...rest} />
      {trailing && <span className={cn(styles.adornment, styles.trailing)}>{trailing}</span>}
    </div>
  );
});

export const Textarea = forwardRef(function Textarea({ invalid, className, rows = 4, ...rest }, ref) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      className={cn(styles.control, styles.textarea, invalid && styles.invalid, className)}
      {...rest}
    />
  );
});

/** Native select (best on mobile, accessible by default) with custom chrome. */
export const Select = forwardRef(function Select(
  { options = [], placeholder, invalid, size = 'md', className, children, ...rest },
  ref
) {
  return (
    <div className={cn(styles.selectWrap, className)}>
      <select
        ref={ref}
        className={cn(styles.control, styles.select, styles[size], invalid && styles.invalid)}
        {...rest}
      >
        {placeholder != null && (
          <option value="" disabled={rest.required}>
            {placeholder}
          </option>
        )}
        {children ||
          options.map((opt) => (
            <option key={opt.key ?? opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </option>
          ))}
      </select>
      <ChevronDown className={styles.chevron} aria-hidden="true" />
    </div>
  );
});

export const Checkbox = forwardRef(function Checkbox({ label, description, className, ...rest }, ref) {
  return (
    <label className={cn(styles.check, className)}>
      <input ref={ref} type="checkbox" className={styles.checkInput} {...rest} />
      <span className={styles.checkBox} aria-hidden="true" />
      <span className={styles.checkText}>
        <span>{label}</span>
        {description && <span className={styles.checkDesc}>{description}</span>}
      </span>
    </label>
  );
});

export function Switch({ checked, onChange, label, disabled, size = 'md', className, ...rest }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={Boolean(checked)}
      aria-label={typeof label === 'string' ? label : undefined}
      disabled={disabled}
      onClick={() => onChange?.(!checked)}
      className={cn(styles.switch, styles[`switch-${size}`], checked && styles.switchOn, className)}
      {...rest}
    >
      <span className={styles.switchThumb} />
    </button>
  );
}
